import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";

const features = [
  "5–7 curated mini-dates per event",
  "Host-led icebreakers & social games",
  "Mutual-matching system (contacts on mutual yes)",
  "Verified attendees with balanced ratio",
  "Premium partner venue experience",
  "No-match credit toward future event",
];

export default function Pricing({ onBookSpot }) {
  return (
    <section
      id="pricing"
      className="py-24 sm:py-32 bg-muted/50"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Explanation */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-mono text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(var(--accent))] mb-4">
              Transparent pricing
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-medium mb-6">
              Pricing & no-match policy
            </h2>
            <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Men pay <strong className="text-foreground">&#8377;1,399 per event</strong>. 
                Women attend <strong className="text-foreground">free</strong> (waitlist-based).
              </p>
              <p>
                Your ticket covers the full curated experience — the venue, professional hosts, 
                icebreaker games, structured mini-dates, and the mutual-matching system. 
                It is not a guarantee of a relationship or a specific number of matches.
              </p>
              <p>
                <strong className="text-foreground">No-match policy:</strong> If you attend an event 
                and receive zero mutual matches, you'll get a one-time discount code toward a 
                future event. No cash refunds.
              </p>
            </div>
          </motion.div>

          {/* Right: Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div
              className="bg-card border border-border/40 rounded-sm p-8 lg:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none"
              data-testid="pricing-card"
            >
              <div className="mb-8">
                <p className="font-mono text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">
                  Per event
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-5xl lg:text-6xl font-medium tracking-tight">
                    &#8377;1,399
                  </span>
                  <span className="text-sm text-muted-foreground">/ men</span>
                </div>
                <p className="text-sm text-[hsl(var(--accent))] font-medium mt-1">
                  Free for women (waitlist)
                </p>
              </div>

              <ul className="space-y-3 mb-8" data-testid="pricing-features-list">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 text-[hsl(var(--accent))] flex-shrink-0" strokeWidth={2} />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                onClick={onBookSpot}
                className="w-full rounded-sm px-8 py-6 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
                data-testid="pricing-book-btn"
              >
                Book your seat
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
